// Shared LiveKit RoomOptions that enable adaptive streaming and dynacast on both
// React Native and web LiveKitRoom wrappers. These flags must be provided when
// constructing the Room instance so that reconnect attempts reuse the same
// configuration.
// --- Reconnexion : pourquoi elle vit ici et nulle part ailleurs -------------
//
// Les écrans de live portaient chacun un timer « pas connecté depuis 6 s →
// room.connect() ». Il tombait *pendant* la reprise (resume) de LiveKit, et le
// SDK répond à un connect() concurrent en jetant la reprise :
// « Reconnection attempt replaced by new connection attempt » → recreateEngine
// → leave envoyé au SFU → toutes les souscriptions et pistes reconstruites.
// Pire, ce timer était armé par un *changement* de `connectionState` : après un
// essai raté, la room retombait sur la valeur qu'elle avait déjà, donc plus rien
// ne se redéclenchait. La room restait morte jusqu'au rechargement de la page.
//
// Ces timers sont supprimés. Il ne restait qu'à combler le trou qui les avait
// fait écrire : la table par défaut du SDK s'arrête au 10ᵉ essai (~44 s cumulées)
// et rend alors `null`, ce qui fait abandonner définitivement — « could not
// recover connection after N attempts, giving up » — puis ferme la room.
//
// On reprend donc **les délais du SDK à l'identique** et on ne supprime que
// l'abandon. Espacer davantage serait un mauvais calcul : ces essais sont
// internes au SDK (pas des rebuilds forcés depuis l'app), le plateau de 7 s est
// le jugement de LiveKit sur ce que le SFU encaisse, et rallonger l'attente
// n'ajoute que du silence en séance après le retour du réseau.
const RETRY_DELAYS_MS = [0, 300, 1200, 2700, 4800]
const RETRY_PLATEAU_MS = 7000 // `maxRetryDelay` du SDK

// Nombre de tentatives du join *initial*. `reconnectPolicy` ne le couvre pas :
// elle ne gouverne que les connexions déjà établies puis perdues. Le défaut du
// SDK est 1, donc un premier join raté restait définitif jusqu'au rechargement.
export const CONNECT_OPTIONS = { maxRetries: 5 }

export const ROOM_OPTIONS = {
    adaptiveStream: true,
    dynacast: true,
    // La politique par défaut du SDK, à un détail près : elle ne rend jamais
    // `null`, donc LiveKit retente indéfiniment au lieu de fermer la room au
    // 10ᵉ essai. Reprise d'abord, ce qui préserve souscriptions et pistes.
    // Le SDK reste seul propriétaire de la reconnexion : l'app n'a aucun
    // `connect()` concurrent à lui opposer.
    reconnectPolicy: {
        // Le jitter est repris du défaut, et il n'est pas cosmétique : sans lui,
        // un redémarrage du SFU fait retenter tous les clients en cadence.
        nextRetryDelayInMs: ({ retryCount }) =>
            (RETRY_DELAYS_MS[retryCount] ?? RETRY_PLATEAU_MS) +
            (retryCount > 1 ? Math.random() * 1000 : 0),
    },
    // Pin to legacy dual-PC mode after livekit-client 2.17 flipped the default
    // to single peer connection. Preserves our prod-tested behavior; to revisit
    // once 2.18 has soaked in production.
    singlePeerConnection: false,
    publishDefaults: {
        simulcast: true,
        videoEncoding: {
            maxBitrate: 1_000_000,
            maxFramerate: 20,
        },
        screenShareEncoding: {
            maxBitrate: 800_000,
            maxFramerate: 15,
        },
    },
}

// Le micro et la caméra choisis au test d'entrée ne passent PAS par ces
// options. La Room est construite une seule fois, au démarrage de
// l'application, bien avant que l'élève ait choisi quoi que ce soit — et
// `useLiveKitRoom` la reconstruirait si ses options sérialisées changeaient,
// ce qui en pleine séance détruirait la salle. Le choix s'applique donc après
// coup, sur la room connectée : voir `PrecallDeviceSync`.
